import { trpc } from "@/lib/trpc";
import { installGlobalErrorInterceptor } from "@/lib/errorInterceptor";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, retryLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

// ── Install global error interceptor (unhandled rejections, offline, etc.) ──
installGlobalErrorInterceptor();

// ── Smart retry: don't retry auth errors or client-side validation errors ──
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    const code = error.data?.code;
    // Never retry auth, forbidden, validation, or not-found errors
    if (
      code === "UNAUTHORIZED" ||
      code === "FORBIDDEN" ||
      code === "BAD_REQUEST" ||
      code === "NOT_FOUND" ||
      code === "PARSE_ERROR"
    ) {
      return false;
    }
  }
  // Retry transient errors up to 2 times (3 total attempts)
  return failureCount < 2;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: 30_000, // 30s default stale time to reduce unnecessary refetches
      refetchOnWindowFocus: false, // Don't refetch on tab switch (prevents data flicker)
    },
    mutations: {
      retry: false, // Never auto-retry mutations (user should re-trigger)
    },
  },
});

// ── Auth redirect on UNAUTHORIZED ───────────────────────────────────────────
const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;
  if (error.message === UNAUTHED_ERR_MSG) {
    window.location.href = getLoginUrl();
  }
};

// ── Surface API errors as toasts ────────────────────────────────────────────
function toastApiError(error: unknown, context: string) {
  redirectToLoginIfUnauthorized(error);

  // Don't double-toast auth errors (they redirect)
  if (error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG) return;

  const msg = error instanceof Error ? error.message : String(error);
  console.error(`[API ${context} Error]`, error);

  // Only toast non-auth errors that users should know about
  if (msg && !msg.includes("10001")) {
    toast.error(`Request failed`, {
      description: msg.slice(0, 150),
      duration: 5000,
    });
  }
}

queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "updated" && event.action.type === "error") {
    toastApiError(event.query.state.error, "Query");
  }
});

queryClient.getMutationCache().subscribe((event) => {
  if (event.type === "updated" && event.action.type === "error") {
    toastApiError(event.mutation.state.error, "Mutation");
  }
});

// ── tRPC client with retryLink for transient network failures ───────────────
const trpcClient = trpc.createClient({
  links: [
    // retryLink handles transient failures (network blips, 502/503)
    retryLink({
      retry(opts) {
        // Only retry on network-level or internal server errors
        if (opts.error.data?.code === "INTERNAL_SERVER_ERROR") {
          return opts.attempts < 2;
        }
        // Don't retry other tRPC errors (they're intentional)
        return false;
      },
    }),
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// ── Listen for error boundary resets to clear stale cache ───────────────────
window.addEventListener("error-boundary-reset", () => {
  queryClient.clear();
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
